import type { DataState } from './dataStore';
import type { ChannelReadState } from '@bunker/shared';
import { passesMuteLevel, unreadUnderMuteLevel } from '@bunker/shared/mutes';
import { getChannelLevel } from '@/lib/muteSettings';

/**
 * Pure unread / read-marker reducers, extracted from the store (same pattern as
 * messageReducers / voiceReducers). Each takes the current state and returns a
 * spread-compatible partial, or the same state reference for a no-op.
 */

/** Set the unread cutoff only on first open; afterwards messages arriving
 *  during the view-session render under the "НОВОЕ" separator. */
export function noteChannelOpenedReducer(
  s: DataState,
  channelId: string,
  newestMessageId: string | null,
): Partial<DataState> | DataState {
  if (channelId in s.lastReadByChannel) return s;
  return { lastReadByChannel: { ...s.lastReadByChannel, [channelId]: newestMessageId } };
}

/** Bump the cutoff to the newest seen — used when leaving a channel. */
export function markChannelReadReducer(
  s: DataState,
  channelId: string,
  newestMessageId: string | null,
): Partial<DataState> {
  return { lastReadByChannel: { ...s.lastReadByChannel, [channelId]: newestMessageId } };
}

/** Increment the unread (and optionally mention) badge for a channel, honouring
 *  the mute level. No-op for the currently-active channel or a fully-muted one.
 *  `guildId` is the channel's server, null for a DM or group. */
export function bumpChannelUnreadReducer(
  s: DataState,
  channelId: string,
  mentioned = false,
  guildId: string | null = null,
  pageVisible = true,
  createdAt?: string,
): Partial<DataState> | DataState {
  // No badge for the channel you're already looking at — but an open channel in
  // a hidden tab / a Desktop window in the tray is not being looked at.
  if (s.currentChannelId === channelId && pageVisible) return s;
  // Already in the server's counts (a read state counted through it).
  const through = s.countedThroughByChannel[channelId];
  if (createdAt && through && Date.parse(createdAt) <= Date.parse(through)) return s;
  // NOTHING suppresses the badge entirely; MENTIONS still bumps it when the
  // user is mentioned (the badge IS the cue that someone called for them).
  const level = getChannelLevel(channelId, guildId);
  if (!passesMuteLevel(level, { isDm: guildId === null, mentioned, repliedTo: false })) return s;
  const cur = s.unreadByChannel[channelId] ?? 0;
  const mentionCur = s.mentionByChannel[channelId] ?? 0;
  const next: Partial<DataState> = {
    unreadByChannel: { ...s.unreadByChannel, [channelId]: cur + 1 },
    mentionByChannel: mentioned
      ? { ...s.mentionByChannel, [channelId]: mentionCur + 1 }
      : s.mentionByChannel,
  };
  // Remember timed bumps so a read state computed before them, but delivered
  // after, adds them back instead of overwriting them.
  if (createdAt) {
    const bumps = [...(s.liveBumpsByChannel[channelId] ?? []), { at: createdAt, mentioned }];
    next.liveBumpsByChannel = { ...s.liveBumpsByChannel, [channelId]: bumps.slice(-LIVE_BUMPS_KEPT) };
  }
  return next;
}

/** Timed live bumps kept per channel — more than any count clients show. */
const LIVE_BUMPS_KEPT = 100;

/** Reset both the unread and mention badge for a channel to 0. */
export function clearChannelUnreadReducer(
  s: DataState,
  channelId: string,
): Partial<DataState> | DataState {
  if (!s.unreadByChannel[channelId] && !s.mentionByChannel[channelId]) return s;
  const { [channelId]: _unreadGone, ...unreadRest } = s.unreadByChannel;
  const { [channelId]: _mentionGone, ...mentionRest } = s.mentionByChannel;
  if (!s.liveBumpsByChannel[channelId]) return { unreadByChannel: unreadRest, mentionByChannel: mentionRest };
  const { [channelId]: _bumpsGone, ...bumpsRest } = s.liveBumpsByChannel;
  return { unreadByChannel: unreadRest, mentionByChannel: mentionRest, liveBumpsByChannel: bumpsRest };
}

/** Replace the read-marker map for a channel (hydration on open). */
export function setReadMarkersReducer(
  s: DataState,
  channelId: string,
  markers: { userId: string; messageId: string; updatedAt: string }[],
): Partial<DataState> {
  const map: Record<string, { messageId: string; updatedAt: string }> = {};
  for (const m of markers) map[m.userId] = { messageId: m.messageId, updatedAt: m.updatedAt };
  return { readMarkersByChannel: { ...s.readMarkersByChannel, [channelId]: map } };
}

/** Advance a single participant's read pointer (live 'channel:read' event),
 *  ignoring out-of-order (older) timestamps. */
export function applyReadMarkerReducer(
  s: DataState,
  channelId: string,
  userId: string,
  messageId: string,
  updatedAt: string,
): Partial<DataState> | DataState {
  const cur = s.readMarkersByChannel[channelId] ?? {};
  const prev = cur[userId];
  // Anti-rewind: ignore an older timestamp (out-of-order WS delivery).
  if (prev && new Date(prev.updatedAt) >= new Date(updatedAt)) return s;
  return {
    readMarkersByChannel: {
      ...s.readMarkersByChannel,
      [channelId]: { ...cur, [userId]: { messageId, updatedAt } },
    },
  };
}

/** The channel's server: its guild id, null for a DM / group, undefined when
 *  this client does not know the channel (left, hidden, not loaded yet). */
function channelGuildId(s: DataState, channelId: string): string | null | undefined {
  if (s.dmChannels[channelId]) return null;
  for (const guild of Object.values(s.guilds)) {
    if (guild.channels.some((c) => c.id === channelId)) return guild.id;
  }
  return undefined;
}

/** Fold server read states into the badge maps, honouring mutes. The open
 *  channel is skipped: it is being read right now. */
function foldReadStates(
  s: DataState,
  states: readonly ChannelReadState[],
): Partial<DataState> | DataState {
  let unread: Record<string, number> | null = null;
  let mention: Record<string, number> | null = null;
  let lastRead: Record<string, string | null> | null = null;
  let through: Record<string, string> | null = null;
  let bumps: DataState['liveBumpsByChannel'] | null = null;
  for (const st of states) {
    if (st.channelId === s.currentChannelId) continue;
    const guildId = channelGuildId(s, st.channelId);
    if (guildId === undefined) continue;
    const shown = unreadUnderMuteLevel(getChannelLevel(st.channelId, guildId), {
      isDm: guildId === null,
      unread: st.unread,
      mentions: st.mentions,
    });
    unread ??= { ...s.unreadByChannel };
    mention ??= { ...s.mentionByChannel };
    lastRead ??= { ...s.lastReadByChannel };
    through ??= { ...s.countedThroughByChannel };
    bumps ??= { ...s.liveBumpsByChannel };
    // Live bumps newer than what the server counted are not in its numbers.
    const cutoff = Date.parse(st.countedThrough);
    const newer = (bumps[st.channelId] ?? []).filter((b) => Date.parse(b.at) > cutoff);
    const total = shown.unread + newer.length;
    const mentions = shown.mentions + newer.filter((b) => b.mentioned).length;
    if (total > 0) unread[st.channelId] = total;
    else delete unread[st.channelId];
    if (mentions > 0) mention[st.channelId] = mentions;
    else delete mention[st.channelId];
    through[st.channelId] = st.countedThrough;
    if (newer.length > 0) bumps[st.channelId] = newer;
    else delete bumps[st.channelId];
    // The «НОВОЕ» divider starts after the stored pointer on the next open.
    lastRead[st.channelId] = st.lastReadMessageId;
  }
  if (!unread || !mention || !lastRead || !through || !bumps) return s;
  return {
    unreadByChannel: unread,
    mentionByChannel: mention,
    lastReadByChannel: lastRead,
    countedThroughByChannel: through,
    liveBumpsByChannel: bumps,
  };
}

/** `ready`: the server's counts replace the live ones for every channel with a
 *  stored pointer (they include what arrived while offline). Channels without
 *  one keep their live counts. */
export function hydrateReadStatesReducer(
  s: DataState,
  states: readonly ChannelReadState[],
): Partial<DataState> | DataState {
  return foldReadStates(s, states);
}

/** `read:state`: my pointer moved on this or another device. */
export function applyReadStateReducer(
  s: DataState,
  state: ChannelReadState,
): Partial<DataState> | DataState {
  return foldReadStates(s, [state]);
}
